import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import PaymentCheckoutTest from "../../CheckoutPage/PaymentCheckoutTest";
import { useAuth } from "../../../auth/AuthContext";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import OrderPricingBreakdownCard from "../../CheckoutPage/OrderPricingBreakdownCard";
import { db } from "../../../configs/firebase";
import WhatsAppPhoneInput from "../../../auth/WhatsappAuth/WhatsAppPhoneInput";
import { SupportedPhoneCountryCode } from "../../../auth/WhatsappAuth/whatsappAuthHelpers";

const removeTrailingSlash = (value: string) => value.replace(/\/+$/, "");
const cardsBorderRadii = "8px";

const getBackendBaseUrl = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname.toLowerCase();

    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0";

    if (isLocal) {
      return removeTrailingSlash(
        String(
          process.env.REACT_APP_LOCAL_BACKEND_URL || "http://localhost:5000",
        ).trim(),
      );
    }
  }

  return removeTrailingSlash(
    String(
      process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_PUBLIC_API_URL ||
        "https://api.pickdengu.mw",
    ).trim(),
  );
};

const BACKEND_BASE_URL = getBackendBaseUrl();

const API_ORDERS = `${BACKEND_BASE_URL}/api/v1/orders`;
const API_TRAVEL = `${BACKEND_BASE_URL}/api/v1/travel/accommodations`;

const SERVICE_FEE_RATE = 0.05;
const GATEWAY_FEE_RATE = 0.025;
const SELLER_COMMISSION_RATE = 0.1;

const roundMoney = (value: number) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const PUBLIC_WEB_URL =
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_WEB_URL ||
  window.location.origin;

/**
 * Temporary default for local phone numbers.
 *
 * For now:
 * 0647367388 -> 27647367388
 *
 * Later, to default local numbers back to Malawi:
 * const DEFAULT_WHATSAPP_COUNTRY_CODE: SupportedPhoneCountryCode = "+265";
 */
const DEFAULT_WHATSAPP_COUNTRY_CODE: SupportedPhoneCountryCode = "+27";

type CreatedOrderPaymentDetails = {
  orderId: string;
  amount: number;
  currency: string;
};

type CustomerProfileData = {
  phone?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  displayName?: string;
  name?: string;
  email?: string;
};

const getPublicBaseUrl = () => String(PUBLIC_WEB_URL || "").replace(/\/+$/, "");

const getAccommodationInvoiceLink = (orderId: string) =>
  `${getPublicBaseUrl()}/invoice/${orderId}`;

const getAccommodationPaymentLink = (orderId: string) =>
  `${getPublicBaseUrl()}/travel/accommodations/checkout/${orderId}`;

const getAccommodationOrderLink = (orderId: string) =>
  `${getPublicBaseUrl()}/book/accommodation/${orderId}`;

function daysBetween(a: string, b: string) {
  const start = new Date(a);
  const end = new Date(b);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const ms = end.getTime() - start.getTime();

  if (!isFinite(ms)) return 0;

  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));

  return Math.max(0, days);
}

function cleanId(value: any) {
  const id = String(value || "").trim();

  if (!id) return "";
  if (id.toLowerCase() === "guest") return "";

  return id;
}

function getLoggedInUserId(user: any) {
  return cleanId(user?.uid || user?._id || "");
}

function getAccommodationOwnerId(accommodation: any) {
  return cleanId(
    accommodation?.ownerId ||
      accommodation?.ownerUserId ||
      accommodation?.createdBy ||
      accommodation?.sellerUserId ||
      accommodation?.userId ||
      "",
  );
}

function getAccommodationOwnerName(accommodation: any) {
  return String(
    accommodation?.ownerName ||
      accommodation?.sellerName ||
      accommodation?.businessName ||
      accommodation?.ownerBusinessName ||
      accommodation?.name ||
      "Accommodation Owner",
  ).trim();
}

function normalizeDigits(value: any) {
  return String(value || "").replace(/\D/g, "");
}

function getCountryDigits(countryCode: SupportedPhoneCountryCode) {
  return normalizeDigits(countryCode);
}

function buildFullPhone(
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) {
  const countryDigits = getCountryDigits(countryCode);
  const localDigits = normalizeDigits(phoneNumber).replace(/^0+/, "");

  if (!countryDigits || !localDigits) return "";

  return `${countryDigits}${localDigits}`;
}

function normalizeLocalPhoneToDefaultCountry(value: any) {
  const digits = normalizeDigits(value);

  if (!digits) return "";

  const defaultCountryDigits = getCountryDigits(DEFAULT_WHATSAPP_COUNTRY_CODE);

  /**
   * Local format:
   * 0647367388
   *
   * If DEFAULT_WHATSAPP_COUNTRY_CODE = "+27":
   * 0647367388 -> 27647367388
   *
   * If DEFAULT_WHATSAPP_COUNTRY_CODE = "+265":
   * 0647367388 -> 265647367388
   */
  if (digits.length === 10 && digits.startsWith("0")) {
    return `${defaultCountryDigits}${digits.slice(1)}`;
  }

  return digits;
}

function normalizeStoredWhatsAppPhone(value: any) {
  const digits = normalizeLocalPhoneToDefaultCountry(value);

  if (!digits) return "";

  return digits;
}

function isValidWhatsAppFullPhone(value: any) {
  const digits = normalizeDigits(value);

  if (digits.startsWith("27")) {
    return digits.length === 11;
  }

  if (digits.startsWith("265")) {
    return digits.length === 12;
  }

  return digits.length >= 10 && digits.length <= 15;
}

function getExpectedLocalPhoneLength(countryCode: SupportedPhoneCountryCode) {
  if (countryCode === "+27") return 9;
  if (countryCode === "+265") return 9;

  return 9;
}

function isValidLocalPhoneInput(
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) {
  const digits = normalizeDigits(phoneNumber).replace(/^0+/, "");
  const expectedLength = getExpectedLocalPhoneLength(countryCode);

  return digits.length >= expectedLength;
}

function getLocalPhoneValidationMessage(
  countryCode: SupportedPhoneCountryCode,
  phoneNumber: string,
) {
  const digits = normalizeDigits(phoneNumber).replace(/^0+/, "");

  if (!digits) {
    return "WhatsApp number is required.";
  }

  if (countryCode === "+27" && digits.length < 9) {
    return "Enter the full South African number, for example 647367388.";
  }

  if (countryCode === "+265" && digits.length < 9) {
    return "Enter the full Malawi number without the country code.";
  }

  const fullPhone = buildFullPhone(countryCode, phoneNumber);

  if (!isValidWhatsAppFullPhone(fullPhone)) {
    return "Please check the country code and enter a valid WhatsApp number.";
  }

  return "";
}

function splitStoredPhoneToInput(value: any): {
  countryCode: SupportedPhoneCountryCode;
  phoneNumber: string;
} {
  const digits = normalizeStoredWhatsAppPhone(value);

  if (digits.startsWith("27")) {
    return {
      countryCode: "+27",
      phoneNumber: digits.slice(2),
    };
  }

  if (digits.startsWith("265")) {
    return {
      countryCode: "+265",
      phoneNumber: digits.slice(3),
    };
  }

  return {
    countryCode: DEFAULT_WHATSAPP_COUNTRY_CODE,
    phoneNumber: digits.startsWith("0") ? digits.slice(1) : digits,
  };
}

function getCustomerName(
  user: any,
  state: any,
  profileData: CustomerProfileData | null,
) {
  return String(
    state?.name ||
      state?.customerName ||
      profileData?.displayName ||
      profileData?.name ||
      user?.displayName ||
      localStorage.getItem("displayName") ||
      "Customer",
  ).trim();
}

const getAccommodationNotificationPolicy = () => ({
  sendCustomerCreated: true,
  sendSellerCreatedStatus: true,
  suppressSellerConfirmReject: true,
  suppressSellerAvailabilityConfirmation: true,
  sellerNotificationMode: "status_view_only",
  sellerCreatedTemplateKey: "seller_order_status",
  sendSellerPaidStatus: true,
  sendCustomerInvoiceOnPaid: true,
  sellerPaidTemplateKey: "seller_order_paid",
  customerPaidTemplateKey: "order_payment_confirmed",
});

export default function AccommodationCheckoutPage() {
  const { theme } = useCustomTheme();
  const { state } = useLocation() as any;
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const createdBookingIdRef = useRef<string | null>(null);
  const createdOrderIdRef = useRef<string | null>(null);
  const createdOrderPaymentRef = useRef<CreatedOrderPaymentDetails | null>(
    null,
  );
  const createOrderPromiseRef =
    useRef<Promise<CreatedOrderPaymentDetails | null> | null>(null);

  const accommodation = state?.accommodation;
  const room = state?.room || null;
  const guests = Number(state?.guests || 1);

  const checkIn: string | null = state?.checkIn || null;
  const checkOut: string | null = state?.checkOut || null;

  const loggedInUserId = getLoggedInUserId(user);
  const buyerUserId = loggedInUserId || "guest";

  const [profileLoading, setProfileLoading] = useState(false);
  const [customerProfile, setCustomerProfile] =
    useState<CustomerProfileData | null>(null);
  const [savedCustomerPhone, setSavedCustomerPhone] = useState("");
  const [invalidSavedCustomerPhone, setInvalidSavedCustomerPhone] =
    useState("");
  const [countryCode, setCountryCode] = useState<SupportedPhoneCountryCode>(
    DEFAULT_WHATSAPP_COUNTRY_CODE,
  );
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);
  const [phoneSaveSuccess, setPhoneSaveSuccess] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);

  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [showWhatsappNumber, setShowWhatsappNumber] = useState(false);
  const [bookingTermsAccepted, setBookingTermsAccepted] = useState(
    Boolean(state?.acceptedTerms),
  );

  const email =
    user?.email ||
    customerProfile?.email ||
    state?.email ||
    "No user email provided";

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      if (!user?.uid) {
        const statePhone =
          state?.phoneNumber ||
          state?.phone ||
          state?.whatsappNumber ||
          localStorage.getItem("phoneNumber") ||
          localStorage.getItem("whatsappNumber") ||
          localStorage.getItem("userPhoneNumber") ||
          "";

        const normalizedStatePhone = normalizeStoredWhatsAppPhone(statePhone);
        const isValidStoredPhone =
          normalizedStatePhone &&
          isValidWhatsAppFullPhone(normalizedStatePhone);

        if (isValidStoredPhone) {
          const splitPhone = splitStoredPhoneToInput(normalizedStatePhone);

          setSavedCustomerPhone(normalizedStatePhone);
          setInvalidSavedCustomerPhone("");
          setCountryCode(splitPhone.countryCode);
          setPhoneNumberInput(splitPhone.phoneNumber);
          setPhoneNumberTouched(false);
          setPhoneSaveSuccess(false);
          return;
        }

        if (normalizedStatePhone) {
          setInvalidSavedCustomerPhone(normalizedStatePhone);
          localStorage.removeItem("phoneNumber");
          localStorage.removeItem("whatsappNumber");
          localStorage.removeItem("userPhoneNumber");
        }

        setSavedCustomerPhone("");
        setCountryCode(DEFAULT_WHATSAPP_COUNTRY_CODE);
        setPhoneNumberInput("");
        setPhoneNumberTouched(false);
        setPhoneSaveSuccess(false);
        return;
      }

      setProfileLoading(true);

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data() as CustomerProfileData;

          const profilePhone = normalizeStoredWhatsAppPhone(
            data.whatsappNumber || data.phoneNumber || data.phone || "",
          );

          const isValidProfilePhone =
            profilePhone && isValidWhatsAppFullPhone(profilePhone);

          setCustomerProfile(data);

          if (isValidProfilePhone) {
            const splitPhone = splitStoredPhoneToInput(profilePhone);

            setSavedCustomerPhone(profilePhone);
            setInvalidSavedCustomerPhone("");
            setCountryCode(splitPhone.countryCode);
            setPhoneNumberInput(splitPhone.phoneNumber);
            setPhoneNumberTouched(false);
            setPhoneSaveSuccess(false);
            return;
          }

          if (profilePhone) {
            setInvalidSavedCustomerPhone(profilePhone);
          }

          setSavedCustomerPhone("");
          setCountryCode(DEFAULT_WHATSAPP_COUNTRY_CODE);
          setPhoneNumberInput("");
          setPhoneNumberTouched(false);
          setPhoneSaveSuccess(false);
        }
      } catch (error) {
        console.error("Failed to fetch customer profile:", error);
        toast({
          title: "Could not load profile phone number",
          description:
            "You can still enter your WhatsApp number before booking.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setProfileLoading(false);
      }
    };

    fetchCustomerProfile();
  }, [user?.uid, state, toast]);

  const inputCustomerPhone = useMemo(
    () => buildFullPhone(countryCode, phoneNumberInput),
    [countryCode, phoneNumberInput],
  );

  const phoneValidationMessage = useMemo(
    () => getLocalPhoneValidationMessage(countryCode, phoneNumberInput),
    [countryCode, phoneNumberInput],
  );

  const isCurrentPhoneValid = !phoneValidationMessage;

  const hasSavedCustomerPhone =
    Boolean(savedCustomerPhone) && isValidWhatsAppFullPhone(savedCustomerPhone);

  const shouldShowPhoneValidation =
    Boolean(phoneValidationMessage) &&
    (phoneNumberTouched || Boolean(invalidSavedCustomerPhone));

  const shouldShowPhoneWarning =
    !profileLoading &&
    (!hasSavedCustomerPhone ||
      shouldShowPhoneValidation ||
      invalidSavedCustomerPhone);

  const customerName = useMemo(
    () => getCustomerName(user, state, customerProfile),
    [user, state, customerProfile],
  );

  const ownerUserId = useMemo(
    () => getAccommodationOwnerId(accommodation),
    [accommodation],
  );

  const ownerName = useMemo(
    () => getAccommodationOwnerName(accommodation),
    [accommodation],
  );

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;

    return daysBetween(checkIn, checkOut);
  }, [checkIn, checkOut]);

  const pricePerNight =
    room?.pricePerNight ??
    accommodation?.pricePerNight ??
    accommodation?.price ??
    0;

  const amount = nights * Number(pricePerNight || 0);

  const pricingBreakdown = useMemo(() => {
    const productSubtotal = roundMoney(Number(amount || 0));
    const serviceFee = roundMoney(productSubtotal * SERVICE_FEE_RATE);
    const checkoutTotal = roundMoney(productSubtotal + serviceFee);
    const gatewayFee = roundMoney(checkoutTotal * GATEWAY_FEE_RATE);
    const sellerCommissionTotal = roundMoney(
      productSubtotal * SELLER_COMMISSION_RATE,
    );
    const sellerNetPayableTotal = roundMoney(
      Math.max(0, productSubtotal - sellerCommissionTotal),
    );
    const platformNetRevenue = roundMoney(
      serviceFee + sellerCommissionTotal - gatewayFee,
    );

    return {
      currency: "MWK",

      productSubtotal,
      serviceFee,
      deliveryFee: 0,
      discountAmount: 0,
      gatewayFee,

      total: checkoutTotal,

      sellerCommissionTotal,
      sellerNetPayableTotal,

      driverEarningTotal: 0,
      deliveryMargin: 0,
      platformNetRevenue,

      deliveryDistanceKm: 0,
      totalActualWeightKg: 0,
      totalVolumetricWeightKg: 0,
      billableWeightKg: 0,
      bulkySurcharge: 0,
    };
  }, [amount]);

  const checkoutAmount = Number(pricingBreakdown.total || amount || 0);

  const phoneDisplayValue = profileLoading
    ? "Checking profile..."
    : phoneNumberInput
      ? inputCustomerPhone
      : savedCustomerPhone || "Missing";

  const handleCountryChange = (value: SupportedPhoneCountryCode) => {
    setCountryCode(value);
    setPhoneNumberInput("");
    setSavedCustomerPhone("");
    setInvalidSavedCustomerPhone("");
    setPhoneNumberTouched(true);
    setPhoneSaveSuccess(false);
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumberInput(value);
    setSavedCustomerPhone("");
    setInvalidSavedCustomerPhone("");
    setPhoneNumberTouched(true);
    setPhoneSaveSuccess(false);
  };

  const saveCustomerPhoneIfNeeded = async () => {
    const fullPhone = buildFullPhone(countryCode, phoneNumberInput);

    if (savedCustomerPhone && !isValidWhatsAppFullPhone(savedCustomerPhone)) {
      setInvalidSavedCustomerPhone(savedCustomerPhone);
      setSavedCustomerPhone("");
    }

    if (!isValidLocalPhoneInput(countryCode, phoneNumberInput)) {
      toast({
        title: invalidSavedCustomerPhone
          ? "Saved WhatsApp number is invalid"
          : "Valid WhatsApp number required",
        description:
          countryCode === "+27"
            ? "Please enter the full local WhatsApp number, for example 647367388."
            : "Please enter the full local WhatsApp number.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      return "";
    }

    if (!isValidWhatsAppFullPhone(fullPhone)) {
      toast({
        title: "Invalid WhatsApp number",
        description:
          "Please check the country code and enter the full WhatsApp number.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      return "";
    }

    setSavingPhone(true);

    try {
      /*
       * Checkout contact only. Do not claim the number and do not update
       * Firebase ownership. This matches the other checkout pages: the number
       * is validated and attached to the booking/order only.
       */
      setSavedCustomerPhone(fullPhone);
      setInvalidSavedCustomerPhone("");
      setPhoneSaveSuccess(true);
      setPhoneNumberTouched(false);

      localStorage.setItem("phoneNumber", fullPhone);
      localStorage.setItem("whatsappNumber", fullPhone);
      localStorage.setItem("userPhoneNumber", fullPhone);

      setCustomerProfile((prev) => ({
        ...(prev || {}),
        phone: fullPhone,
        phoneNumber: fullPhone,
        whatsappNumber: fullPhone,
      }));

      toast({
        title: "WhatsApp number saved for checkout",
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      return fullPhone;
    } finally {
      setSavingPhone(false);
    }
  };

  const handleSaveWhatsAppNumber = async () => {
    setPhoneNumberTouched(true);

    if (!isCurrentPhoneValid) {
      toast({
        title: "Invalid WhatsApp number",
        description: phoneValidationMessage,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });

      return;
    }

    await saveCustomerPhoneIfNeeded();
  };

  const handlePhoneInputKeyDown = async (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    await handleSaveWhatsAppNumber();
  };

  const createAccommodationOrder =
    async (): Promise<CreatedOrderPaymentDetails | null> => {
      if (createdOrderPaymentRef.current && createdBookingIdRef.current) {
        return createdOrderPaymentRef.current;
      }

      if (!accommodation) {
        throw new Error("Accommodation details are missing.");
      }

      if (!bookingTermsAccepted) {
        throw new Error(
          "Please confirm the accommodation booking details and terms before payment.",
        );
      }

      const resolvedPhoneNumber = await saveCustomerPhoneIfNeeded();

      if (
        !resolvedPhoneNumber ||
        !isValidWhatsAppFullPhone(resolvedPhoneNumber)
      ) {
        setShowWhatsappNumber(true);
        throw new Error(
          "Please add a valid WhatsApp number before continuing with payment.",
        );
      }

      if (!ownerUserId) {
        throw new Error(
          "This accommodation listing is missing a valid owner ID. Please edit and save the accommodation again from the owner account before accepting bookings.",
        );
      }

      if (loggedInUserId && ownerUserId === loggedInUserId) {
        const confirmed = window.confirm(
          "You are booking your own accommodation listing. Continue?",
        );

        if (!confirmed) {
          throw new Error("Accommodation booking was cancelled.");
        }
      }

      if (!checkIn || !checkOut || nights <= 0) {
        throw new Error(
          "Missing booking dates. Please go back and choose check-in and check-out dates.",
        );
      }

      const roomId = room?.id || "default";

      try {
        const availRes = await axios.get(
          `${API_TRAVEL}/${accommodation._id}/availability`,
          { params: { checkIn, checkOut, guests } },
        );

        const availItem = (availRes.data?.availability || []).find(
          (a: any) => a.roomId === roomId,
        );
        const available = Number(availItem?.available || 0);

        if (available <= 0) {
          throw new Error(
            "This room is sold out for the selected dates. Please choose different dates or another room.",
          );
        }
      } catch (e: any) {
        if (e?.message?.includes("sold out")) {
          throw e;
        }

        console.error("Availability check failed:", e);
        throw new Error("Could not verify availability. Please try again.");
      }

      const title = `${accommodation.name}${room?.title ? ` — ${room.title}` : ""}`;
      const displayImage =
        room?.imageUrl ||
        accommodation.imageUrl ||
        accommodation.images?.[0] ||
        "";

      const accommodationId = String(
        accommodation._id || accommodation.id || "",
      );
      const locationCoords = accommodation.locationCoords || undefined;
      const notificationPolicy = getAccommodationNotificationPolicy();

      let orderId = createdOrderIdRef.current;
      let paymentDetails = createdOrderPaymentRef.current;

      /*
       * Match product and delivery checkout:
       * persist the pending order first and reuse that same order on retries.
       * The accommodation booking is then linked to the persisted order ID.
       */
      if (!orderId || !paymentDetails) {
        const payload = {
          category: "accommodation",
          serviceCategory: "accommodation",
          orderType: "accommodation",
          checkoutMode: "pickup",
          source: "web",
          channel: "web",

          userId: buyerUserId,
          email,
          phoneNumber: resolvedPhoneNumber,
          name: customerName,

          ownerUserId,
          sellerUserId: ownerUserId,
          sellerName: ownerName,

          fromAddress: accommodation.location || "",
          toAddress: accommodation.location || "",

          fromLocation: locationCoords,
          toLocation: locationCoords,

          pickupAddress: accommodation.location || "",

          payment: {
            method: "",
            provider: "",
            status: "pending",
            providerPaymentId: "",
            providerCheckoutId: "",
            reference: "",
          },

          notificationPolicy,

          items: [
            {
              _id: accommodationId,
              productId: accommodationId,

              sellerUserId: ownerUserId,
              ownerId: ownerUserId,
              sellerBusinessName: ownerName,

              title,
              description: accommodation.location || "",
              category: "accommodation",
              serviceCategory: "accommodation",
              orderType: "accommodation",
              imageUrl: displayImage,

              price: Number(pricePerNight || 0),
              quantity: Number(nights),

              selectedVariants: [],
              variantSummary: room?.title || "",

              isPhysical: false,
              weightKg: 0,
              lengthCm: 0,
              widthCm: 0,
              heightCm: 0,
              bulky: false,

              meta: {
                accommodationId,
                roomId,
                roomTitle: room?.title || "",
                checkIn,
                checkOut,
                nights,
                guests,
                ownerUserId,
                customerName,
                customerPhone: resolvedPhoneNumber,
                bookingId: "",
                category: "accommodation",
                serviceCategory: "accommodation",
                orderType: "accommodation",
              },
            },
          ],

          total: checkoutAmount,
          currency: "MWK",
          pricingBreakdown,

          deliveryInstructions: `Accommodation booking: ${checkIn} → ${checkOut} (${nights} nights)`,

          // Accommodation is fulfilled by the property owner. It must never
          // create a marketplace dispatch job or resolve warehouses.
          skipDispatchCreation: true,
          requiresWarehouse: false,
          requiresDispatch: false,

          metadata: {
            source: "web",
            channel: "web",
            category: "accommodation",
            serviceCategory: "accommodation",
            orderType: "accommodation",

            accommodationId,
            roomId,
            roomTitle: room?.title || "",
            propertyName: accommodation.name || "",
            location: accommodation.location || "",
            checkIn,
            checkOut,
            nights,
            guests,

            ownerUserId,
            ownerName,
            customerName,
            customerPhone: resolvedPhoneNumber,
            bookingId: "",

            linkMode: "order_id",
            invoicePath: "invoice",
            paymentPath: "travel/accommodations/checkout",
            trackingPath: "book/accommodation",
            checkInPath: "book/accommodation",

            notificationPolicy,
            requiresWarehouse: false,
            requiresDispatch: false,
          },
        };

        const orderRes = await axios.post(API_ORDERS, payload);
        const createdOrder =
          orderRes.data?.data || orderRes.data?.order || orderRes.data || {};

        orderId =
          createdOrder?._id ||
          orderRes.data?.data?._id ||
          orderRes.data?.order?._id ||
          orderRes.data?._id ||
          null;

        if (!orderId) {
          throw new Error(
            "The order was created but no order ID was returned.",
          );
        }

        let persistedOrder = createdOrder;

        /*
         * Read the order once after creation so PayChangu receives the exact
         * total persisted by the backend.
         */
        try {
          const savedOrderRes = await axios.get(
            `${API_ORDERS}/${encodeURIComponent(orderId)}`,
          );

          persistedOrder =
            savedOrderRes.data?.data ||
            savedOrderRes.data?.order ||
            savedOrderRes.data ||
            createdOrder;
        } catch (savedOrderError) {
          console.warn(
            "Could not reload the created accommodation order; using the create response.",
            savedOrderError,
          );
        }

        const createdOrderAmount = Number(
          persistedOrder?.total ||
            createdOrder?.total ||
            persistedOrder?.pricingBreakdown?.total ||
            createdOrder?.pricingBreakdown?.total ||
            checkoutAmount,
        );

        paymentDetails = {
          orderId,
          amount:
            Number.isFinite(createdOrderAmount) && createdOrderAmount > 0
              ? createdOrderAmount
              : checkoutAmount,
          currency: String(
            persistedOrder?.currency ||
              persistedOrder?.pricingBreakdown?.currency ||
              createdOrder?.currency ||
              createdOrder?.pricingBreakdown?.currency ||
              "MWK",
          )
            .trim()
            .toUpperCase(),
        };

        // Persist immediately so a booking retry reuses the same order.
        createdOrderIdRef.current = orderId;
        createdOrderPaymentRef.current = paymentDetails;
      }

      if (!orderId || !paymentDetails) {
        throw new Error(
          "Could not prepare the accommodation order for payment.",
        );
      }

      const invoiceLink = getAccommodationInvoiceLink(orderId);
      const paymentLink = getAccommodationPaymentLink(orderId);
      const trackingLink = getAccommodationOrderLink(orderId);

      if (!createdBookingIdRef.current) {
        try {
          const bookingRes = await axios.post(
            `${API_TRAVEL}/accommodation-bookings`,
            {
              accommodationId,
              roomId,
              checkIn,
              checkOut,
              roomsBooked: 1,
              guests,

              userId: buyerUserId,
              ownerUserId,

              orderId,

              status: "confirmed",
              paymentStatus: "pending",
              paymentMethod: "",
              paymentProvider: "",
              paymentReference: "",

              source: "web",
              channel: "web",
              category: "accommodation",
              serviceCategory: "accommodation",
              orderType: "accommodation",

              customerPhone: resolvedPhoneNumber,
              customerName,
              guestPhone: resolvedPhoneNumber,
              guestName: customerName,
              guestEmail: email,

              paymentLink,
              invoiceLink,
              trackingLink,
              checkInLink: trackingLink,

              metadata: {
                source: "web",
                channel: "web",
                category: "accommodation",
                serviceCategory: "accommodation",
                orderType: "accommodation",

                orderId,
                accommodationId,
                roomId,
                roomTitle: room?.title || "",
                propertyName: accommodation.name || "",
                location: accommodation.location || "",
                checkIn,
                checkOut,
                nights,
                guests,

                customerPhone: resolvedPhoneNumber,
                customerName,
                ownerUserId,
                ownerName,

                invoiceLink,
                paymentLink,
                trackingLink,
                checkInLink: trackingLink,
              },
            },
          );

          const bookingId =
            bookingRes.data?._id ||
            bookingRes.data?.data?._id ||
            bookingRes.data?.booking?._id ||
            null;

          if (!bookingId) {
            throw new Error(
              "The accommodation booking was not created. Please try again.",
            );
          }

          createdBookingIdRef.current = bookingId;
        } catch (e: any) {
          if (e?.response?.status === 409) {
            throw new Error(
              "The room became sold out. Please choose different dates or another room.",
            );
          }

          console.error("Booking create failed:", e?.response?.data || e);

          throw new Error(
            e?.response?.data?.error ||
              e?.response?.data?.message ||
              e?.message ||
              "Could not create the accommodation booking. Please try again.",
          );
        }
      }

      return paymentDetails;
    };

  const onCreateOrder =
    async (): Promise<CreatedOrderPaymentDetails | null> => {
      if (createdOrderPaymentRef.current) {
        return createdOrderPaymentRef.current;
      }

      if (createOrderPromiseRef.current) {
        return createOrderPromiseRef.current;
      }

      const promise = createAccommodationOrder();
      createOrderPromiseRef.current = promise;

      try {
        return await promise;
      } finally {
        createOrderPromiseRef.current = null;
      }
    };

  if (!accommodation) {
    return (
      <Box p={6} color={theme.text}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={6}
          boxShadow={theme.shadowSm}
        >
          <Heading size="md" color={theme.text}>
            Accommodation not found
          </Heading>

          <Text mt={2} color={theme.textSubtle}>
            Open from the accommodation details “Book” button.
          </Text>

          <Button
            mt={4}
            onClick={() => navigate("/dashboard")}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            boxShadow={theme.shadowSm}
          >
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} mx="auto" color={theme.text}>
      <Box
        border="1px solid"
        borderColor={theme.border}
        bg={theme.surface}
        borderRadius={cardsBorderRadii}
        p={{ base: 4, md: 6 }}
        boxShadow={theme.shadowSm}
      >
        <Stack spacing={4}>
          {(!checkIn || !checkOut || nights <= 0) && (
            <Alert status="warning" borderRadius={cardsBorderRadii}>
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Booking dates are missing.</Text>
                <Text fontSize="sm">
                  Please go back and choose valid check-in and check-out dates
                  before continuing with payment.
                </Text>
              </Box>
            </Alert>
          )}

          {!ownerUserId && (
            <Alert status="warning" borderRadius={cardsBorderRadii}>
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  This listing has no valid owner ID.
                </Text>
                <Text fontSize="sm">
                  Edit and save the accommodation again from the owner account
                  before accepting bookings.
                </Text>
              </Box>
            </Alert>
          )}

          {shouldShowPhoneWarning && (
            <Alert
              status={
                shouldShowPhoneValidation || invalidSavedCustomerPhone
                  ? "error"
                  : "warning"
              }
              borderRadius={cardsBorderRadii}
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">
                  {invalidSavedCustomerPhone
                    ? "Saved WhatsApp number is invalid."
                    : "WhatsApp number is required."}
                </Text>
                <Text fontSize="sm">
                  {invalidSavedCustomerPhone ? (
                    <>
                      The saved number <b>{invalidSavedCustomerPhone}</b> is
                      incomplete or invalid. Open the WhatsApp number section
                      below and save a valid number.
                    </>
                  ) : (
                    <>
                      Open the WhatsApp number section below and save a valid
                      number so the booking confirmation, invoice, payment link,
                      and check-in link can be sent to the customer.
                    </>
                  )}
                </Text>
              </Box>
            </Alert>
          )}

          <Box
            border="1px solid"
            borderColor={theme.border}
            bg={theme.surfaceAlt}
            borderRadius={cardsBorderRadii}
            overflow="hidden"
          >
            <Stack
              as="button"
              type="button"
              direction={{ base: "column", sm: "row" }}
              w="100%"
              textAlign="left"
              justify="space-between"
              align={{ base: "stretch", sm: "center" }}
              spacing={3}
              p={{ base: 3, sm: 4 }}
              cursor="pointer"
              _hover={{ bg: theme.hover }}
              onClick={() => setShowBookingDetails((prev) => !prev)}
            >
              <Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="purple" borderRadius={cardsBorderRadii} px={3} py={1}>
                    Accommodation Checkout
                  </Badge>

                  <Text fontWeight="800" color={theme.text}>
                    Booking Details
                  </Text>
                </HStack>

                <Text mt={1} fontSize="sm" color={theme.textSubtle}>
                  {accommodation.name} • {nights} night
                  {nights === 1 ? "" : "s"} • MWK{" "}
                  {checkoutAmount.toLocaleString()}
                </Text>
              </Box>

              <HStack
                spacing={2}
                flexShrink={0}
                alignSelf={{ base: "flex-end", sm: "auto" }}
              >
                <Text fontSize="sm" fontWeight="700" color={theme.textMuted}>
                  {showBookingDetails ? "Hide" : "Show"}
                </Text>

                <ChevronDownIcon
                  color={theme.textMuted}
                  transition="transform 0.2s ease"
                  transform={
                    showBookingDetails ? "rotate(180deg)" : "rotate(0deg)"
                  }
                />
              </HStack>
            </Stack>

            <Collapse in={showBookingDetails} animateOpacity>
              <Stack spacing={4} px={4} pb={4}>
                <Stack spacing={1}>
                  <Heading size="lg" color={theme.text}>
                    {accommodation.name}
                  </Heading>

                  <Text color={theme.textSubtle}>{accommodation.location}</Text>
                </Stack>

                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacing={3}
                  p={{ base: 3, sm: 4 }}
                  border="1px solid"
                  borderColor={theme.border}
                  borderRadius={cardsBorderRadii}
                  bg={theme.surface}
                >
                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Room selected
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {room?.title || "Default room"}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Stay
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {checkIn || "-"} → {checkOut || "-"}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Guests
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {guests}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Customer
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {customerName}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      WhatsApp
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {phoneDisplayValue}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Owner
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {ownerName}
                    </Text>
                  </Box>
                </SimpleGrid>

                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacing={3}
                  p={{ base: 3, sm: 4 }}
                  border="1px solid"
                  borderColor={theme.border}
                  borderRadius={cardsBorderRadii}
                  bg={theme.surface}
                >
                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Nights
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      {nights}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Price / night
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      MWK {Number(pricePerNight || 0).toLocaleString()}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Owner payout estimate
                    </Text>

                    <Text fontWeight="bold" color={theme.text}>
                      MWK{" "}
                      {Number(
                        pricingBreakdown.sellerNetPayableTotal || 0,
                      ).toLocaleString()}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="sm" color={theme.textSubtle}>
                      Total
                    </Text>

                    <Text fontWeight="bold" fontSize="lg" color={theme.text}>
                      MWK {checkoutAmount.toLocaleString()}
                    </Text>
                  </Box>
                </SimpleGrid>

                <OrderPricingBreakdownCard
                  breakdown={pricingBreakdown}
                  fallbackTotal={checkoutAmount}
                  title="Booking Breakdown"
                />
              </Stack>
            </Collapse>
          </Box>

          <Box
            border="1px solid"
            borderColor={shouldShowPhoneWarning ? "orange.300" : theme.border}
            bg={theme.surfaceAlt}
            borderRadius={cardsBorderRadii}
            overflow="hidden"
          >
            <HStack
              as="button"
              type="button"
              w="100%"
              textAlign="left"
              justify="space-between"
              align="center"
              spacing={3}
              p={4}
              cursor="pointer"
              _hover={{ bg: theme.hover }}
              onClick={() => setShowWhatsappNumber((prev) => !prev)}
            >
              <Box>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge
                    colorScheme={
                      shouldShowPhoneWarning
                        ? "orange"
                        : hasSavedCustomerPhone
                          ? "green"
                          : "gray"
                    }
                    borderRadius={cardsBorderRadii}
                    px={3}
                    py={1}
                  >
                    WhatsApp
                  </Badge>

                  <Text fontWeight="800" color={theme.text}>
                    Customer WhatsApp Number
                  </Text>
                </HStack>

                <Text mt={1} fontSize="sm" color={theme.textSubtle}>
                  Number: <b>{phoneDisplayValue}</b>
                </Text>
              </Box>

              <HStack spacing={2} flexShrink={0}>
                <Text fontSize="sm" fontWeight="700" color={theme.textMuted}>
                  {showWhatsappNumber ? "Hide" : "Show"}
                </Text>

                <ChevronDownIcon
                  color={theme.textMuted}
                  transition="transform 0.2s ease"
                  transform={
                    showWhatsappNumber ? "rotate(180deg)" : "rotate(0deg)"
                  }
                />
              </HStack>
            </HStack>

            <Collapse in={showWhatsappNumber} animateOpacity>
              <Box px={4} pb={4}>
                <Alert
                  status={
                    shouldShowPhoneValidation || invalidSavedCustomerPhone
                      ? "error"
                      : hasSavedCustomerPhone
                        ? "info"
                        : "warning"
                  }
                  borderRadius={cardsBorderRadii}
                >
                  <AlertIcon />
                  <Box w="100%">
                    <HStack
                      justify="space-between"
                      align="flex-start"
                      spacing={3}
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      <Box>
                        <Text fontWeight="bold">Customer WhatsApp number</Text>

                        <Text fontSize="sm" mb={3}>
                          {hasSavedCustomerPhone
                            ? "Confirm or edit the WhatsApp number before payment. Booking confirmation, invoice, payment link, and check-in link will be sent to this number."
                            : "Add a valid WhatsApp number so the booking confirmation, invoice, payment link, and check-in link can be sent to the customer."}
                        </Text>
                      </Box>

                      <Button
                        size="sm"
                        w={{ base: "100%", md: "auto" }}
                        onClick={handleSaveWhatsAppNumber}
                        isLoading={savingPhone}
                        isDisabled={!isCurrentPhoneValid || savingPhone}
                        colorScheme={isCurrentPhoneValid ? "green" : "gray"}
                        flexShrink={0}
                      >
                        ✓ Save
                      </Button>
                    </HStack>

                    <Box onKeyDown={handlePhoneInputKeyDown}>
                      <WhatsAppPhoneInput
                        isRequired
                        label="WhatsApp number"
                        countryCode={countryCode}
                        phoneNumber={phoneNumberInput}
                        onCountryCodeChange={handleCountryChange}
                        onPhoneNumberChange={handlePhoneNumberChange}
                        labelColor={theme.textMuted}
                        inputStyles={{
                          bg: theme.inputBg,
                          color: theme.text,
                          borderColor:
                            shouldShowPhoneValidation ||
                            invalidSavedCustomerPhone
                              ? "red.400"
                              : phoneSaveSuccess
                                ? "green.400"
                                : theme.inputBorder,
                          _placeholder: {
                            color: theme.inputPlaceholder,
                          },
                          _hover: {
                            borderColor:
                              shouldShowPhoneValidation ||
                              invalidSavedCustomerPhone
                                ? "red.400"
                                : theme.borderStrong,
                          },
                          _focus: {
                            borderColor:
                              shouldShowPhoneValidation ||
                              invalidSavedCustomerPhone
                                ? "red.400"
                                : theme.inputBorderFocus,
                            boxShadow:
                              shouldShowPhoneValidation ||
                              invalidSavedCustomerPhone
                                ? "0 0 0 1px red"
                                : `0 0 0 1px ${theme.inputBorderFocus}`,
                          },
                        }}
                      />
                    </Box>

                    {shouldShowPhoneValidation && (
                      <Text mt={2} fontSize="sm" color="red.500">
                        {phoneValidationMessage}
                      </Text>
                    )}

                    {!shouldShowPhoneValidation && phoneSaveSuccess && (
                      <Text mt={2} fontSize="sm" color="green.500">
                        WhatsApp number saved. Notifications will be sent to{" "}
                        <b>{savedCustomerPhone}</b>.
                      </Text>
                    )}

                    {!shouldShowPhoneValidation &&
                      !phoneSaveSuccess &&
                      isCurrentPhoneValid &&
                      phoneNumberInput && (
                        <Text mt={2} fontSize="sm" color={theme.textMuted}>
                          Press <b>Enter</b> or tap <b>✓ Save</b> to update this
                          number.
                        </Text>
                      )}
                  </Box>
                </Alert>
              </Box>
            </Collapse>
          </Box>

          <Box>
            <Button
              w="100%"
              size="lg"
              h="auto"
              py={4}
              whiteSpace="normal"
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              borderRadius={cardsBorderRadii}
              fontWeight="800"
              leftIcon={bookingTermsAccepted ? <CheckIcon /> : undefined}
              aria-pressed={bookingTermsAccepted}
              aria-describedby="accommodation-confirmation-help"
              isDisabled={savingPhone}
              onClick={() => setBookingTermsAccepted((accepted) => !accepted)}
            >
              {bookingTermsAccepted
                ? "Accommodation Details Confirmed"
                : "Confirm Accommodation Details"}
            </Button>
            <Text
              id="accommodation-confirmation-help"
              mt={2}
              fontSize="sm"
              color={theme.textMuted}
              aria-live="polite"
            >
              {bookingTermsAccepted
                ? "Details confirmed. You can now continue to payment below."
                : "Confirm your dates, room and number of guests to enable payment."}
            </Text>
          </Box>

          <Box>
            <fieldset
              disabled={!bookingTermsAccepted || savingPhone}
              aria-disabled={!bookingTermsAccepted || savingPhone}
              style={{
                border: 0,
                padding: 0,
                margin: 0,
                minWidth: 0,
                opacity: bookingTermsAccepted ? 1 : 0.55,
                pointerEvents: bookingTermsAccepted ? "auto" : "none",
                transition: "opacity 0.2s ease",
              }}
            >
              <PaymentCheckoutTest
                amount={checkoutAmount}
                onCreateOrder={onCreateOrder}
                isLoading={savingPhone}
                shouldClearCart={false}
                pendingPath={(id: string) => `/checkout-pending/${id}`}
                successPath={(id: string) => `/checkout-success/${id}`}
              />
            </fieldset>
          </Box>

          {savingPhone && (
            <Text fontSize="sm" color={theme.textMuted}>
              Saving WhatsApp number...
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
